import React, { useEffect, useState } from 'react';
import { axios, toast, API_URL, useDispatch, useSelector, config } from '../../../Data/Imports';
import { getLabelTypeDropdown } from '../../../Redux/features/dropdown/dropdownSlice';

const AddLabel = () => {
	const dispatch = useDispatch();
	const { labelTypeDropdown, isLoading } = useSelector((state) => state.dropdown);
	const [hasFetched, setHasFetched] = useState(false);

	useEffect(() => {
		if (labelTypeDropdown?.length === 0 && !isLoading && !hasFetched) {
			setHasFetched(true);
			dispatch(getLabelTypeDropdown());
		}
	}, [dispatch, isLoading, labelTypeDropdown?.length, hasFetched]);

	const [formData, setFormData] = useState({
		LabelNames: [],
		LabelTypeId: '',
		LabelName: '',
		LabelHeading: '',
	});

	const [masterData, setMasterData] = useState({
		MasterTypeId: 7,
		MasterValue: '',
	});

	const [loading, setLoading] = useState(false);
	const [formType, setFormType] = useState('label');

	const fields = [
		{
			id: 'LabelHeading',
			label: 'Label Heading',
			name: 'LabelHeading',
			type: 'text',
			placeholder: 'Enter Label Heading',
		},
		{
			id: 'LabelName',
			label: 'Label Name',
			name: 'LabelName',
			type: 'text',
			placeholder: 'Enter Label Name',
		},
	];

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleMasterChange = (e) => {
		const { name, value } = e.target;
		setMasterData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleFormTypeChange = (e) => {
		setFormType(e.target.value);
		setFormData({
			LabelNames: [],
			LabelTypeId: '',
			LabelName: '', // Reset the input field when form type changes
		});
	};

	const handleAddLabel = () => {
		// Ensure the label is not already added and is not empty
		if (
			formData.LabelNames.some((labelObj) => labelObj.Label.LabelName === formData.LabelName) ||
			!formData.LabelName.trim() ||
			!formData.LabelHeading.trim()
		) {
			return;
		}

		// Add the label with the structure { Label: { LabelHeading, LabelName } }
		setFormData((prevData) => ({
			...prevData,
			LabelNames: [
				...prevData.LabelNames,
				{ Label: { LabelHeading: formData.LabelHeading, LabelName: formData.LabelName } }, // Added LabelHeading
			],
			LabelName: '', // Reset input field after adding
			LabelHeading: '',
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);

		try {
			if (!formData.LabelNames.length || !formData.LabelTypeId) {
				setLoading(false);
				return;
			}

			const labelsData = {
				LabelNames: formData.LabelNames,
				LabelTypeId: formData.LabelTypeId,
			};

			const response = await axios.post(`${API_URL}/label/post/add-label`, labelsData, config);

			if (response?.data?.success) {
				setFormData({
					LabelNames: [],
					LabelTypeId: '',
					LabelHeading: '',
					LabelName: '',
				});
				toast.success('Labels added successfully!');
			}
		} catch (error) {
			setLoading(false);
			toast.error(error?.response?.data?.message || 'Something went wrong.');
		} finally {
			setLoading(false);
		}
	};

	const handleMasterSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);

		try {
			if (!masterData.MasterValue) {
				setLoading(false);
				return;
			}

			const response = await axios.post(
				`${API_URL}/master/post/add-master-entry`,
				masterData,
				config
			);

			if (response?.data?.success) {
				setMasterData((prevData) => ({
					...prevData,
					MasterValue: '',
				}));
				toast.success('Label type added successfully!');
			}
		} catch (error) {
			setLoading(false);
			toast.error(error?.response?.data?.message || 'Something went wrong.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className='w-full max-w-xl mx-auto p-6 bg-white rounded-md shadow-lg mt-10'>
			<h1 className='text-2xl font-semibold mb-6 text-center text-gray-800'>
				Add {formType === 'label' ? 'Label' : 'Label Type'}
			</h1>

			{/* Form Type Radio Buttons */}
			<div className='flex gap-10 mb-6'>
				<label className='flex items-center'>
					<input
						type='radio'
						name='formType'
						value='label'
						checked={formType === 'label'}
						onChange={handleFormTypeChange}
						className='mr-2'
					/>
					Add Label
				</label>
				<label className='flex items-center'>
					<input
						type='radio'
						name='formType'
						value='labelType'
						checked={formType === 'labelType'}
						onChange={handleFormTypeChange}
						className='mr-2'
					/>
					Add Label Type
				</label>
			</div>

			{/* Form */}

			{formType === 'label' ? (
				<form onSubmit={handleSubmit} className='space-y-6'>
					{/* Label Type Dropdown */}
					<div>
						<label htmlFor='LabelTypeId' className='block text-sm font-medium text-gray-700 mb-2'>
							Label Type
						</label>
						<select
							id='LabelTypeId'
							name='LabelTypeId'
							value={formData.LabelTypeId}
							onChange={handleChange}
							required
							className='w-full p-3 border-2 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'>
							<option value=''>Select a Label Type</option>
							{labelTypeDropdown?.map((labelType) => (
								<option key={labelType.Id} value={labelType.Id}>
									{labelType.MasterValue}
								</option>
							))}
						</select>
					</div>

					{/* Label Name Input */}
					{fields.map((field) => (
						<div key={field.id}>
							<label htmlFor={field.id} className='block text-sm font-medium text-gray-700 mb-2'>
								{field.label}
							</label>
							<input
								id={field.id}
								name={field.name}
								placeholder={field.placeholder}
								type={field.type}
								value={formData[field.name]}
								onChange={handleChange}
								className='w-full p-3 border-2 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
							/>
						</div>
					))}
					{/* Button to Add Label */}
					<button
						type='button'
						onClick={handleAddLabel}
						className='w-full py-3 text-white bg-green-500 rounded-md hover:bg-green-600 transition duration-300 focus:outline-none focus:ring-2 focus:ring-green-500'>
						Add Another Label
					</button>

					{/* Display the added labels */}
					{formData.LabelNames.length > 0 && (
						<div className='mt-4'>
							<h2 className='text-sm font-medium text-gray-700 mb-2'>Added Labels:</h2>
							<div className='overflow-x-auto'>
								<table className='w-full border-collapse border border-gray-300'>
									<thead>
										<tr className='bg-gray-200 text-gray-700'>
											<th className='border border-gray-300 px-4 py-2 text-left'>Label Heading</th>
											<th className='border border-gray-300 px-4 py-2 text-left'>Label Name</th>
										</tr>
									</thead>
									<tbody>
										{formData.LabelNames.map((labelObj, index) => (
											<tr key={index} className='text-gray-600'>
												<td className='border border-gray-300 px-4 py-2'>
													{labelObj.Label.LabelHeading}
												</td>
												<td className='border border-gray-300 px-4 py-2'>
													{labelObj.Label.LabelName}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}

					{/* Submit Button */}
					<button
						type='submit'
						disabled={loading}
						className='w-full py-3 text-white bg-blue-500 rounded-md hover:bg-blue-600 transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400'>
						{loading ? 'Adding...' : 'Submit'}
					</button>
				</form>
			) : (
				<form onSubmit={handleMasterSubmit} className='space-y-6'>
					{/* Label Type Name Input */}
					<div>
						<label htmlFor='MasterValue' className='block text-sm font-medium text-gray-700 mb-5'>
							Enter Label Type Name
						</label>
						<input
							id='MasterValue'
							name='MasterValue'
							type='text'
							placeholder='Enter Label Type'
							value={masterData.MasterValue}
							onChange={handleMasterChange}
							required
							className='w-full p-3 border-2 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
						/>
					</div>
					{/* Submit Button */}
					<button
						type='submit'
						disabled={loading}
						className='w-full py-3 text-white bg-blue-500 rounded-md hover:bg-blue-600 transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400'>
						{loading ? 'Adding...' : 'Submit'}
					</button>
				</form>
			)}
		</div>
	);
};

export default AddLabel;
